var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { GroupingState, IntegratedGrouping, IntegratedSelection, SelectionState, SortingState } from '@devexpress/dx-react-grid';
import { Grid, Table, TableGroupRow, TableHeaderRow, TableRowDetail, TableSelection, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';
import classNames from 'classnames';
import { lowerCase, upperCase } from 'lodash';
import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { PermissionsCheck } from '../Components/Auth/PermissionsCheck';
import { Button, ButtonMode } from '../Components/Button';
import { LoadMoreFooter } from '../Components/DataTable/LoadMoreFooter';
import { FloatingLinearProgress } from '../Components/FloatingLinearProgress';
import { CloudUploadIcon, EditPenIcon, ReassignVerbalOrderIcon, RefreshIcon, ReviewVerbalOrderIcon, StopHandIcon, TrashCanIcon } from '../Components/Icons';
import { useMedicationSettings } from '../Config/MedicationConfig';
import { MedicationCourse, MedicationStatus, PatientMedicationAllowedAction } from '../Contracts/MedicationModels';
import { usePermissions } from '../Hooks/usePermissions';
import { MedicationsPermissions } from '../MedicationsPermissions';
import { orange, useCssColours } from '../Styles/Colours';
import styles from './MedicationSummaryDataTable.module.css';
/** Changes the scroll mode for the grid */
export var ScrollMode;
(function (ScrollMode) {
    /**
     * @description Virtual scrolling increases performance by rendering results in a known window size.
     * It is useful when there are many results as user is unable to view most of the data and therefore there is no benefit rendering it.
     */
    ScrollMode["virtual"] = "virtual";
    /**
     * @description Normal scrolling disables virtual scrolling which means all results will be rendered.
     * Using normal scrolling can be beneficial when you have only a few results where scrolling doesnt occur often
     * NOTE: Using normal scroll mode disables sticky headers.
     */
    ScrollMode["normal"] = "normal";
})(ScrollMode || (ScrollMode = {}));
function RootComponent(props) {
    return (React.createElement(Grid.Root, __assign({}, props, { style: {
            width: '100%'
        } })));
}
export function StyledTableCell(props) {
    var _a;
    var cellStyles = classNames(styles.cell, (_a = {},
        _a[styles.interactive] = props.onClick !== undefined,
        _a[styles.courseScriptNumber] = props.column.name === 'courseScriptNumber',
        _a[styles.courseInstructions] = props.column.name === 'courseInstructions',
        _a));
    var onClick = function (event) {
        if (props.onClick === undefined)
            return;
        if (props.row === undefined)
            return;
        event.preventDefault();
        event.stopPropagation();
        props.onClick(props.row);
    };
    return React.createElement(Table.Cell, __assign({}, props, { className: cellStyles, onClick: onClick, "data-test-id": props.column.name }));
}
var TableComponent = function (_a) {
    var props = __rest(_a, []);
    return (React.createElement(Table.Table, __assign({}, props, { className: styles.table, "data-test-id": 'datatable-medication-summary-table' })));
};
var ContainerComponent = function (_a) {
    var props = __rest(_a, []);
    return React.createElement(Table.Container, __assign({}, props, { className: styles.muiTableContainer }));
};
function TableBody(props) {
    return React.createElement(Table.TableBody, __assign({}, props, { className: styles.body }));
}
function HeaderCell(props) {
    return React.createElement(TableHeaderRow.Cell, __assign({}, props, { className: styles.headerCell }));
}
var SortingIcon = function (_a) {
    var _b;
    var direction = _a.direction, active = _a.active;
    return (React.createElement("span", { className: classNames(styles.sortingIcon, (_b = {},
            _b[styles.active] = active,
            _b)) }, direction === 'asc' || direction === null ? (React.createElement(ArrowUpward, { style: { fontSize: '18px' } })) : (React.createElement(ArrowDownward, { style: { fontSize: '18px' } }))));
};
var SortLabel = function (props) {
    var _a;
    var align = props.align, column = props.column, direction = props.direction, disabled = props.disabled, onSort = props.onSort;
    var labelRight = align == 'right';
    var columnBeingSorted = direction !== null;
    var sortableColumn = !disabled;
    return (React.createElement("div", { className: classNames(styles.sortLabel, (_a = {},
            _a[styles.sortableColumn] = sortableColumn,
            _a[styles.sortLabelLeft] = sortableColumn && !labelRight,
            _a[styles.sortLabelRight] = sortableColumn && labelRight,
            _a)), onClick: function () {
            if (sortableColumn) {
                onSort({
                    direction: direction === 'asc' ? 'desc' : 'asc',
                    keepOther: false
                });
            }
        } },
        labelRight && sortableColumn && React.createElement(SortingIcon, { direction: direction, active: columnBeingSorted }),
        React.createElement(TableHeaderRow.Title, null, column.title),
        !labelRight && sortableColumn && React.createElement(SortingIcon, { direction: direction, active: columnBeingSorted })));
};
function HeaderCellComponent(props) {
    var _a;
    return (React.createElement(TableSelection.HeaderCell, __assign({}, props, { className: classNames(styles.headerCellComponent, (_a = {},
            _a[styles.headerCellComponentSelected] = props.someSelected,
            _a)) })));
}
function HeaderRow(props) {
    return React.createElement(TableHeaderRow.Row, __assign({}, props, { className: styles.headerRow }));
}
function GroupRow(props) {
    var _a, e_1, _b;
    var status = props.rowProps.row.value;
    var doNotColour = [MedicationStatus.Stopped, MedicationStatus.EnteredInError];
    var countClass = classNames((_a = {},
        _a[styles.countNew] = !doNotColour.includes(status),
        _a[styles.countError] = status === MedicationStatus.EnteredInError,
        _a));
    var total = 0;
    var draftTotal = 0;
    try {
        for (var _c = __values(props.otherProps.data), _d = _c.next(); !_d.done; _d = _c.next()) {
            var item = _d.value;
            if (lowerCase(item.grouping) === lowerCase(status)) {
                total++;
                if (item.lastMedication.status === MedicationStatus.Draft) {
                    draftTotal++;
                }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var draftText;
    if (draftTotal > 1) {
        draftText = draftTotal + ' medications to finalise';
    }
    else {
        draftText = draftTotal + ' medication to finalise';
    }
    return (React.createElement("tr", { className: styles.groupRow },
        React.createElement("td", { className: styles.groupCell, colSpan: 11 },
            upperCase(status),
            " (",
            React.createElement("span", { className: countClass }, total),
            ")\u00A0",
            draftTotal > 0 && (React.createElement("span", { className: styles.draftText },
                React.createElement(CloudUploadIcon, { style: { verticalAlign: 'bottom' }, color: orange, size: '16' }),
                " ",
                draftText)))));
}
function TableHeaderStub(props) {
    return React.createElement(Table.StubHeaderCell, __assign({}, props, { className: styles.headerStub }));
}
function CellStub(props) {
    return React.createElement(Table.StubCell, __assign({}, props, { className: styles.cellStub }));
}
// FIXME: Currently waiting for dev extreme to update their type interfaces for TableSelection
function TableRow(props) {
    var _a;
    var _b, _c, _d, _e, _f, _g;
    var _h = __read(useState(false), 2), rowHover = _h[0], setRowHover = _h[1];
    var handleTableRowMouseEvents = function (event) {
        if (event.type === 'mouseover' || event.type === 'mouseenter') {
            setRowHover(true);
        }
        else if (event.type === 'mouseleave') {
            setRowHover(false);
        }
    };
    var medCode = (_d = (_c = (_b = props.row) === null || _b === void 0 ? void 0 : _b.productPack) === null || _c === void 0 ? void 0 : _c.code) !== null && _d !== void 0 ? _d : '';
    var medName = ((_g = (_f = (_e = props.row) === null || _e === void 0 ? void 0 : _e.productPack) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : '').substr(0, 20);
    return (React.createElement(Table.Row, __assign({}, props, { "data-test-med-code": medCode, "data-test-med-name": medName, className: classNames(styles.row, (_a = {}, _a[styles.rowHover] = rowHover, _a)), onMouseOver: function (event) { return handleTableRowMouseEvents(event); }, onMouseEnter: function (event) { return handleTableRowMouseEvents(event); }, onMouseLeave: function (event) { return handleTableRowMouseEvents(event); } })));
}
export var AllowedActionButtons = function (props) {
    var _a, _b;
    var permissions = usePermissions({
        stopMedications: MedicationsPermissions.StopMedications,
        prescribeMedicationsTakeHomeScript: MedicationsPermissions.PrescribeMedicationsTakeHomeScript,
        prescribeMedicationsEnrmcScript: MedicationsPermissions.PrescribeMedicationsEnrmcScript,
        prescribeMedicationsMedicationOrder: MedicationsPermissions.PrescribeMedicationsMedicationOrder,
        prescribeMedicationsMedicationHistory: MedicationsPermissions.PrescribeMedicationsMedicationHistory,
        prescribeComplementaryTherapyOrder: MedicationsPermissions.PrescribeComplementaryTherapyOrder
    });
    if (!props.allowedActions || props.allowedActions.length === 0)
        return React.createElement(React.Fragment, null);
    var medicationSettings = useMedicationSettings();
    var canReprescribe = [
        permissions.prescribeMedicationsTakeHomeScript,
        permissions.prescribeMedicationsEnrmcScript,
        permissions.prescribeMedicationsMedicationOrder,
        permissions.prescribeMedicationsMedicationHistory,
        permissions.prescribeComplementaryTherapyOrder
    ].filter(Boolean).length > 0 && !!permissions.stopMedications;
    var editButton = medicationSettings.settings.modalMedications && (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.editMedication();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Edit', testId: 'menuItem_Edit' },
        React.createElement(EditPenIcon, { className: styles.blueButton, color: useCssColours, size: '16' })));
    var adjustButton = (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.adjustMedication();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Adjust', testId: 'menuItem_Adjust' },
        React.createElement(EditPenIcon, { className: styles.blueButton, color: useCssColours, size: '16' })));
    var deleteButton = (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.deleteMedication();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Delete', testId: 'menuItem_Delete' },
        React.createElement(TrashCanIcon, { className: styles.redButton, color: useCssColours, size: '16' })));
    var reviewVerbalOrderButton = (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.reviewVerbalOrder();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: "Review " + ((_a = props.verbalOrderTerm) === null || _a === void 0 ? void 0 : _a.abbreviatedSingularTerm), testId: 'menuItem_ReviewVTO' },
        React.createElement(ReviewVerbalOrderIcon, { className: styles.blueButton, color: useCssColours, size: '24' })));
    var reassignVerbalOrderButton = (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.reassignVerbalOrder();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: "Reassign " + ((_b = props.verbalOrderTerm) === null || _b === void 0 ? void 0 : _b.abbreviatedSingularTerm), testId: 'menuItem_ReassignVTO' },
        React.createElement(ReassignVerbalOrderIcon, { className: styles.blueButton, color: useCssColours, size: '24' })));
    var stopButton = (React.createElement(PermissionsCheck, { key: 'stop-medication', permission: MedicationsPermissions.StopMedications },
        React.createElement(Button, { onClick: function (e) {
                e.preventDefault();
                e.stopPropagation();
                props.stopMedication();
            }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Stop', testId: 'menuItem_Stop' },
            React.createElement(StopHandIcon, { className: styles.redButton, color: useCssColours, size: '24' }))));
    var represcribeButton = (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.represcribeMedication();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Re-prescribe', testId: 'menuItem_Represcribe' },
        React.createElement(RefreshIcon, { className: styles.blueButton, color: useCssColours, size: '24' })));
    return (React.createElement("div", null,
        props.allowedActions.includes(PatientMedicationAllowedAction.Adjust) && adjustButton,
        props.allowedActions.includes(PatientMedicationAllowedAction.Delete) && deleteButton,
        props.allowedActions.includes(PatientMedicationAllowedAction.Stop) && stopButton,
        props.allowedActions.includes(PatientMedicationAllowedAction.Represcribe) &&
            canReprescribe &&
            represcribeButton,
        props.allowedActions.includes(PatientMedicationAllowedAction.ReassignVto) && reassignVerbalOrderButton,
        props.allowedActions.includes(PatientMedicationAllowedAction.ReviewVto) && reviewVerbalOrderButton));
};
var RowComponent = function (props) {
    var _a;
    var _b, _c, _d, _e, _f, _g;
    var selectByRowClick = props.selectByRowClick, highlighted = props.highlighted, onToggle = props.onToggle, otherProps = __rest(props, ["selectByRowClick", "highlighted", "onToggle"]);
    var rowStyles = classNames(styles.row, (_a = {}, _a[styles.selected] = highlighted, _a));
    var medCode = (_d = (_c = (_b = props.tableRow.row) === null || _b === void 0 ? void 0 : _b.productPack) === null || _c === void 0 ? void 0 : _c.code) !== null && _d !== void 0 ? _d : '';
    var medName = ((_g = (_f = (_e = props.tableRow.row) === null || _e === void 0 ? void 0 : _e.productPack) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : '').substr(0, 20);
    return (React.createElement(Table.Row, __assign({}, otherProps, { "data-test-med-code": medCode, "data-test-med-name": medName, row: props.tableRow.row, onClick: props.onToggle, className: rowStyles })));
};
export function MedicationSummaryDataTable(props) {
    var _a, _b, _c, _d, _e;
    var containerRef = useRef(null);
    var sanitisedScrollMode = (_a = props.scrollMode) !== null && _a !== void 0 ? _a : ScrollMode.virtual;
    function ToggleCell(toggleCellProps) {
        var expanded = toggleCellProps.expanded, onToggle = toggleCellProps.onToggle, others = __rest(toggleCellProps, ["expanded", "onToggle"]);
        var onClick = function (event) {
            event.stopPropagation();
            event.preventDefault();
            onToggle();
        };
        return React.createElement(TableRowDetail.Cell, __assign({}, others, { className: styles.detailCell }));
    }
    var handleSelection = useCallback(function (selectedIds) {
        var _a;
        if ((_a = props.rowChecking) === null || _a === void 0 ? void 0 : _a.onSelectedChanged) {
            props.rowChecking.onSelectedChanged(selectedIds);
        }
    }, [props.data, (_b = props.rowChecking) === null || _b === void 0 ? void 0 : _b.selectedIds]);
    var limitedData = !!Number(props.limit) && props.limit !== undefined
        ? props.data.filter(function (it, index) {
            return !!Number(props.limit) && props.limit !== undefined ? index < props.limit : true;
        })
        : props.data;
    var showLoadMoreFooter = !!props.limit && props.data.length > props.limit;
    var SelectionCellComponent = props.customCheckbox
        ? function (selectionProps) {
            var onToggle = selectionProps.onToggle, tableColumn = selectionProps.tableColumn, tableRow = selectionProps.tableRow, row = selectionProps.row, selected = selectionProps.selected, otherProps = __rest(selectionProps, ["onToggle", "tableColumn", "tableRow", "row", "selected"]);
            var cellStyles = classNames(styles.cell, styles.selectionCell);
            return (React.createElement("td", __assign({}, otherProps, { className: cellStyles }), props.customCheckbox(row, selected, onToggle)));
        }
        : undefined;
    var tableContainerClasses = classNames(styles.tableContainer);
    return (React.createElement("div", { className: tableContainerClasses, ref: containerRef },
        React.createElement(FloatingLinearProgress, { display: props.loading }),
        React.createElement(Grid, { rows: limitedData, columns: props.columns, getRowId: props.getRowId, rootComponent: RootComponent },
            React.createElement(SortingState, { sorting: props.sorting, defaultSorting: props.defaultSort, columnExtensions: props.sortingControl, onSortingChange: props.onSortingChange }),
            React.createElement(GroupingState, { grouping: [{ columnName: 'course' }], expandedGroups: [
                    MedicationCourse.AsRequired,
                    MedicationCourse.Regular,
                    MedicationCourse.ShortCourse,
                    MedicationCourse.None
                ] }),
            React.createElement(IntegratedGrouping, null),
            sanitisedScrollMode === ScrollMode.virtual ? (React.createElement(VirtualTable, { columnExtensions: props.columnExtensions, height: 'auto', cellComponent: props.cellComponent, bodyComponent: TableBody, rowComponent: TableRow, stubHeaderCellComponent: TableHeaderStub, stubCellComponent: CellStub, estimatedRowHeight: 67, tableComponent: TableComponent, containerComponent: ContainerComponent })) : (React.createElement(Table, { columnExtensions: props.columnExtensions, cellComponent: props.cellComponent, bodyComponent: TableBody, rowComponent: TableRow, stubHeaderCellComponent: TableHeaderStub, stubCellComponent: CellStub, tableComponent: TableComponent, containerComponent: ContainerComponent })),
            !!props.rowChecking && (React.createElement(SelectionState, { selection: (_d = (_c = props.rowChecking) === null || _c === void 0 ? void 0 : _c.selectedIds) !== null && _d !== void 0 ? _d : [], onSelectionChange: handleSelection })),
            React.createElement(TableHeaderRow, { cellComponent: HeaderCell, showSortingControls: true, rowComponent: HeaderRow, sortLabelComponent: SortLabel }),
            !!((_e = props.rowChecking) === null || _e === void 0 ? void 0 : _e.showSelectAll) && React.createElement(IntegratedSelection, null),
            !!props.rowChecking && (React.createElement(TableSelection, { showSelectionColumn: props.rowChecking.showSelectionColumn, showSelectAll: props.rowChecking.showSelectAll, rowComponent: RowComponent, headerCellComponent: HeaderCellComponent, cellComponent: SelectionCellComponent })),
            React.createElement(TableGroupRow, { indentColumnWidth: 1, rowComponent: function (rowProps) { return React.createElement(GroupRow, { rowProps: rowProps, otherProps: props }); } })),
        showLoadMoreFooter && React.createElement(LoadMoreFooter, { showing: props.limit })));
}
